import { FC } from 'react';
import { CustomModal, SaveChangesModal } from '@components';
import { Controller, Form, FormProvider } from 'react-hook-form';
import {
  Box,
  Button,
  FormControlLabel,
  FormGroup,
  Switch,
  TextField,
} from '@mui/material';
import { t } from 'i18next';
import { isNil } from 'lodash';
import { Select } from '@ui';
import { useAddReportFolderModal } from '@views/pages/Reporting/ReportFolders/hooks';

interface AddReportFolderModalProps {
  isOpen: boolean;
  handleClose: () => void;
}

export const AddReportFolderModal: FC<AddReportFolderModalProps> = ({
  isOpen,
  handleClose,
}) => {
  const {
    isAddReportFolderPending,
    isValid,
    organizationOptions,
    onSubmit,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isEditSubmitting,
    handleCloseAction,
  } = useAddReportFolderModal(handleClose);

  const { control } = formMethods;

  return (
    <>
      <CustomModal
        isOpen={isOpen}
        onClose={handleCloseAction}
        title={t('reporting.addFolderModal.title')}
        isLoading={isAddReportFolderPending}
      >
        <FormProvider {...formMethods}>
          <Form
            control={control}
            onSubmit={e => onSubmit(e.event)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <Controller
              name='organization_id'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Select
                  {...field}
                  label={t('reporting.addFolderModal.organization')}
                  error={!isNil(error)}
                  required
                  options={organizationOptions}
                  helperText={error?.message}
                />
              )}
            />

            <Controller
              name='name'
              control={control}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type='text'
                  label={t('reporting.addFolderModal.name')}
                  variant='outlined'
                  error={!isNil(error)}
                  helperText={error ? error.message : null}
                  required
                  fullWidth
                />
              )}
            />

            <Controller
              name='isPrivate'
              control={control}
              render={({ field }) => (
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={(
                          e: React.ChangeEvent<HTMLInputElement>,
                          checked,
                        ) => field.onChange(checked)}
                      />
                    }
                    label={t('reporting.addFolderModal.private')}
                  />
                </FormGroup>
              )}
            />

            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '8px',
                position: 'sticky',
                bottom: 0,
                left: 0,
                backgroundColor: 'white',
                pb: 2,
              }}
            >
              <Button
                variant='text'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                }}
                disabled={isAddReportFolderPending}
                onClick={handleCloseAction}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type='submit'
                variant='contained'
                color='secondary'
                sx={{
                  fontSize: '0,875rem',
                  fontWeight: 'medium',
                  lineHeight: '24px',
                  color: 'white',
                }}
                disabled={isAddReportFolderPending || !isValid}
              >
                {t('general.ADD')}
              </Button>
            </Box>
          </Form>
        </FormProvider>
      </CustomModal>
      <SaveChangesModal
        isOpen={isSaveChangesModalOpen}
        onClose={handleCloseSaveChangesModal}
        isValid={isValid}
        handleSaveChanges={handleSaveChanges}
        discardChanges={handleDiscardChanges}
        isLoading={isEditSubmitting}
      />
    </>
  );
};
